"use client"

import { Check, ChevronDown, Copy } from "lucide-react"
import { useEffect, useState } from "react"

import { accountText, type LocaleProps } from "../../personal-web"
import { hashLpaPrefix, parseLpa } from "../lpa"

type CopyableField =
  | "lpa"
  | "smdp"
  | "activation_code"

type Props = LocaleProps & {
  lpa: string
  /** Called with the field id whenever the user hits Copy — used for analytics. */
  onCopy?: (field: CopyableField) => void
  /**
   * Called once on mount if the LPA can't be split into SM-DP+ + Activation
   * Code (parseLpa returned null). Lets the parent emit
   * install_lpa_parse_failed so we can monitor spec-noncompliant LPAs in
   * production without exposing the raw string.
   */
  onParseFailed?: (info: { lpa_length: number; lpa_prefix_hash: string }) => void
}

/**
 * Manual install card.
 *
 * Backend only stores a single `lpa` string (Strapi `esim.lpa`). Earlier
 * versions of this card pretended SM-DP+ / Matching ID / Confirmation
 * Code were independent fields and rendered empty rows when they didn't
 * exist. Now we treat LPA as the source of truth and parse it on demand
 * for users whose phones ask for SM-DP+ and Activation Code separately.
 *
 * Layout:
 *   - Primary row: LPA / Activation Code (full string + Copy)
 *   - Collapsed `<details>`: "Need SM-DP+ and Activation Code separately?"
 *     Inside: a short explanation that these come from the LPA, plus the
 *     parsed SM-DP+ + Activation Code rows with their own Copy buttons.
 *   - If parseLpa(lpa) returns null (spec-noncompliant LPA), the details
 *     block isn't rendered — fail-soft. Caller's onParseFailed fires so
 *     the issue shows up in analytics instead of being silent.
 */
export function ManualCard({ lng, lpa, onCopy, onParseFailed }: Props) {
  const parsed = parseLpa(lpa)

  // Fire parse-failed analytics exactly once per mount when relevant.
  useEffect(() => {
    if (!lpa || parsed) return
    onParseFailed?.({
      lpa_length: lpa.length,
      lpa_prefix_hash: hashLpaPrefix(lpa),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lpa])

  if (!lpa) {
    return (
      <section className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-5 text-center text-sm text-slate-600">
        {accountText(lng, "install.manualUnavailable")}
      </section>
    )
  }

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6">
      <FieldRow
        lng={lng}
        labelKey="install.fieldLpa"
        value={lpa}
        fieldId="lpa"
        onCopy={onCopy}
      />

      {parsed ? (
        <details className="group mt-4 overflow-hidden rounded-xl border border-stone-200">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-stone-50 [&::-webkit-details-marker]:hidden">
            <span>{accountText(lng, "install.manualCard.splitToggle")}</span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-stone-500 transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <div className="border-t border-stone-100 p-4">
            <div className="grid gap-4">
              <FieldRow
                lng={lng}
                labelKey="install.fieldSmdp"
                value={parsed.smdp}
                fieldId="smdp"
                onCopy={onCopy}
              />
              <FieldRow
                lng={lng}
                labelKey="install.fieldActivationCode"
                value={parsed.activationCode}
                fieldId="activation_code"
                onCopy={onCopy}
              />
            </div>
          </div>
        </details>
      ) : null}
    </section>
  )
}

function FieldRow({
  lng,
  labelKey,
  value,
  fieldId,
  onCopy,
}: LocaleProps & {
  labelKey: string
  value: string
  fieldId: CopyableField
  onCopy?: (field: CopyableField) => void
}) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard?.writeText(value)
      setCopied(true)
      onCopy?.(fieldId)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = value
      ta.style.position = "fixed"
      ta.style.left = "-9999px"
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand("copy")
        setCopied(true)
        onCopy?.(fieldId)
        window.setTimeout(() => setCopied(false), 1800)
      } catch {
        // Give up silently — the user can still long-press to copy.
      }
      document.body.removeChild(ta)
    }
  }

  return (
    <div className="grid gap-1.5">
      <dt className="text-xs font-bold uppercase tracking-wider text-stone-500">
        {accountText(lng, labelKey)}
      </dt>
      <dd className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg bg-stone-100 px-3 py-2.5 font-sans text-[13px] text-slate-900">
          {value}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
            copied
              ? "bg-emerald-50 text-emerald-700"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
          aria-live="polite"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden />
              {accountText(lng, "install.copied")}
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" aria-hidden />
              {accountText(lng, "install.copyShort")}
            </>
          )}
        </button>
      </dd>
    </div>
  )
}
